import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
  useMemo
} from "react";
import { useFormTyped } from "shared/hooks";
import {
  CreateCRMPropertyRequest,
  PropertyFormData,
  PropertyKind
} from "./types";
import { createPropertyValidationSchema } from "./validation";
import { useMutation } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { useNavigate } from "react-router-dom";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { blankUnit } from "./constants";
import { useAuthContext } from "providers/Auth";
import { PartnerFeatures } from "shared/types";
import { normalizeFilename } from "shared/utils";

const DEFAULT_FORM_VALUES: PropertyFormData = {
  name: "",
  kind: null,
  description: "",
  external_id: "",
  owner_name: undefined,

  address1: "",
  address2: "",
  city: "",
  state_or_province: "",
  postal_code: "",
  country: "US",

  use_property_group_verification_template: false,
  verification_template_id: "",

  monthly_rent_amount: undefined,
  home_rent_amount: undefined,
  site_rent_amount: undefined,
  available_at: new Date().toISOString().split("T")[0],
  occupied: false,
  unit_external_id: "",
  listing_id: "",

  sqft: undefined,
  beds: undefined,
  baths: undefined,

  amenities: [],

  cats_allowed: false,
  dogs_allowed: false,

  media: [],

  units: [blankUnit]
};

interface PropertyProviderProps {
  children: ReactNode;
}

interface CreatePropertyResponse {
  id: string;
  [key: string]: unknown;
}

const transformToAPIData = (
  formData: PropertyFormData,
  sessionKey: string,
  homeSiteRentEnabled: boolean = false
): CreateCRMPropertyRequest => {
  const singleFamilyFields = {
    units: [
      {
        title: formData.name,
        ...(homeSiteRentEnabled &&
        formData.home_rent_amount !== undefined &&
        formData.site_rent_amount !== undefined
          ? {
              monthly_rent_amount: {
                cents: Math.round(
                  parseFloat(String(formData.home_rent_amount)) * 100 +
                    parseFloat(String(formData.site_rent_amount)) * 100
                ),
                currency: "USD"
              },
              home_rent_amount: {
                cents: Math.round(
                  parseFloat(String(formData.home_rent_amount)) * 100
                ),
                currency: "USD"
              },
              site_rent_amount: {
                cents: Math.round(
                  parseFloat(String(formData.site_rent_amount)) * 100
                ),
                currency: "USD"
              }
            }
          : {
              monthly_rent_amount:
                formData.monthly_rent_amount && formData.monthly_rent_amount > 0
                  ? {
                      cents: Math.round(formData.monthly_rent_amount * 100),
                      currency: "USD"
                    }
                  : undefined
            }),
        available_at: formData.available_at || undefined,
        occupied: formData.occupied ?? false,
        external_id: formData.unit_external_id || undefined,
        listing_id: formData.listing_id || undefined,
        plan: {
          sqft: formData.sqft && formData.sqft > 0 ? formData.sqft : undefined,
          beds: formData.beds && formData.beds > 0 ? formData.beds : undefined,
          baths:
            formData.baths && formData.baths > 0 ? formData.baths : undefined
        },
        ...(formData.sale_price
          ? {
              sale_price: {
                cents: Math.round(
                  parseFloat(String(formData.sale_price)) * 100
                ),
                currency: "USD"
              }
            }
          : {}),
        ...(formData.dimensions ? { dimensions: formData.dimensions } : {})
      }
    ]
  };

  const defaultFields = {
    units: formData.units.reduce(
      (acc, unit) => {
        if (unit.title && unit.monthly_rent_amount !== null)
          acc.push({
            title: unit.title,
            monthly_rent_amount: {
              cents: unit.monthly_rent_amount,
              currency: "USD"
            },
            available_at: unit.available_at || undefined,
            occupied: unit.occupied,
            external_id: unit.external_id || undefined
          });
        else if (
          homeSiteRentEnabled &&
          unit.title &&
          unit.home_rent_amount !== null &&
          unit.site_rent_amount !== null
        )
          acc.push({
            title: unit.title,
            monthly_rent_amount: {
              cents: unit.home_rent_amount + unit.site_rent_amount,
              currency: "USD"
            },
            home_rent_amount: {
              cents: unit.home_rent_amount,
              currency: "USD"
            },
            site_rent_amount: {
              cents: unit.site_rent_amount,
              currency: "USD"
            },
            available_at: unit.available_at || undefined,
            occupied: unit.occupied,
            external_id: unit.external_id || undefined
          });

        return acc;
      },
      [] as NonNullable<CreateCRMPropertyRequest["units"]>
    )
  };

  return {
    media_session_key: sessionKey,
    name: formData.name,
    kind: formData.kind,
    description: formData.description || undefined,
    external_id: formData.external_id || undefined,
    owner_name: formData.owner_name || undefined,
    address: {
      address1: formData.address1,
      address2: formData.address2 || undefined,
      city: formData.city,
      state_or_province: formData.state_or_province,
      postal_code: formData.postal_code,
      country: formData.country,
      lat: formData.lat,
      lng: formData.lng,
      name: formData.address_name
    },
    use_property_group_verification_template:
      formData.use_property_group_verification_template ?? true,
    verification_template_id: formData.verification_template_id || undefined,
    amenity_ids: formData.amenities,
    pet_policy: {
      cats_allowed: formData.cats_allowed,
      dogs_allowed: formData.dogs_allowed
    },
    media: formData.media || [],

    ...(formData.kind === PropertyKind.SingleFamily ? singleFamilyFields : {}),
    ...(formData.kind === null ? defaultFields : {})
  };
};

interface PropertyPresignedUrlResponse {
  asset: {
    content_type: string;
    global_id: string;
    id: number;
    original_filename: string;
    size: number;
    url: string;
  };
  response: {
    url: string;
  };
}

interface PropertyFormContextType {
  values: PropertyFormData;
  errors: Partial<Record<keyof PropertyFormData, string>>;
  setErrors: (errors: Partial<Record<keyof PropertyFormData, string>>) => void;
  isSubmitting: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register: any;
  handleSubmit: (e: React.FormEvent) => void;
  handleChange: (
    key: keyof PropertyFormData,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    customMap?: (value: any, prev: any) => any,
    mask?: string
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => (e: any) => void;
  getPropertyPresigned: (file: File) => Promise<PropertyPresignedUrlResponse>;
  sessionKey: string;
  canSubmit: boolean;
  setPhotosUploading: (isUploading: boolean) => void;
}

const PropertyFormContext = createContext<PropertyFormContextType | undefined>(
  undefined
);

const sessionKey = `${Date.now()}-${crypto.randomUUID()}`;

export const PropertyFormProvider = ({ children }: PropertyProviderProps) => {
  const dictionary = useUnitsPropertiesDictionary();
  const { partnerFeatures, pms, type, partner } = useAuthContext();
  const navigate = useNavigate();
  const [photosUploading, setPhotosUploading] = useState(false);

  const {
    values,
    register,
    onSubmit,
    handleChange,
    isSubmitting,
    errors,
    setErrors
  } = useFormTyped<PropertyFormData>(
    {
      ...DEFAULT_FORM_VALUES,
      kind: (partner?.property_type as PropertyKind | undefined) ?? null
    },
    createPropertyValidationSchema({
      hasPropertyOwner: partnerFeatures.includes(PartnerFeatures.PropertyOwner),
      isBoomScreen: type.isBoomScreen
    })
  );

  const homeSiteRentEnabled = pms.isManageAmerica || type.isHomeSiteRentEnabled;

  const canSubmit = useMemo(() => {
    return !photosUploading;
  }, [photosUploading]);

  const { mutate: createProperty, isPending: isCreatingProperty } = useMutation(
    {
      mutationFn: (data: CreateCRMPropertyRequest) =>
        instance.post<CreatePropertyResponse>("/portal/v1/properties", data),
      onSuccess: ({ data }) => {
        toast.success("Property created successfully!");
        if (values.kind === PropertyKind.MultiFamily)
          navigate(`/properties/${data.id}`, {
            state: { defaultTab: dictionary.Units }
          });
        else navigate(`/properties`);
      },
      onError: (error) => {
        toast.error("Error creating property: " + getErrorMessage(error));
      }
    }
  );

  const getPropertyPresigned = useCallback(
    async (file: File) => {
      const queryParams = new URLSearchParams({
        session_key: sessionKey,
        size: file.size.toString(),
        filename: normalizeFilename(file.name),
        type: file.type
      }).toString();

      const { data } = await instance(
        `/portal/v1/listings/assets/presign?${queryParams}`
      );

      return data;
    },
    [sessionKey]
  );

  const handleSubmit = onSubmit({
    onSuccess: (formData) => {
      const data = transformToAPIData(
        formData,
        sessionKey,
        homeSiteRentEnabled
      );
      createProperty(data);
    },
    onError: () => {
      toast.error("Form validation errors");
    }
  });

  const contextValue: PropertyFormContextType = {
    values,
    isSubmitting: isSubmitting || isCreatingProperty,
    register,
    handleSubmit,
    handleChange,
    errors,
    getPropertyPresigned,
    sessionKey,
    canSubmit,
    setPhotosUploading,
    setErrors
  };

  return (
    <PropertyFormContext.Provider value={contextValue}>
      {children}
    </PropertyFormContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const usePropertyFormContext = () => {
  const context = useContext(PropertyFormContext);
  if (context === undefined)
    throw new Error(
      "usePropertyFormContext must be used within a PropertyFormProvider"
    );
  return context;
};
